import React from "react";
import { Accordion, Button } from "react-bootstrap";
import InputText from "../../Components/sectioninput/formelement/InputText";
import CustomButton from "../../Components/Common/CustomButton";
import { useProductsFR } from "../../hooks/product-fr";
import SimpleUploader from "../../Components/Common/SimpleUploader";
import Switch from "../../Components/Common/Switch";
import { Form } from "react-bootstrap";
import {
  collections,
  saunaCollections,
  spaDeNageCollections,
  types,
} from "../../Components/Products/productsData";
import Textarea from "../../Components/sectioninput/formelement/Textarea";

const SectionContainer = ({
  id,
  titleColor = "black",
  title,
  elements = [],
  formKey,
  formData,
  setFormData,
}) => {
  const { updateProduct } = useProductsFR();

  const initializeFormData = () => {
    if (!formData[formKey]) {
      setFormData({
        ...formData,
        [formKey]: {},
      });
    }
  };

  React.useEffect(() => {
    initializeFormData();
  }, [formKey]);

  const handleInfoChange = (value, name) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleChange = (fieldKey, value) => {
    setFormData({
      ...formData,
      [formKey]: {
        ...(formData[formKey] || {}),
        [fieldKey]: value,
      },
    });
  };

  // New function to handle multiple images for a field
  const handleMultipleImages = (fieldKey, file, index = null) => {
    const currentImages = Array.isArray(formData[formKey]?.[fieldKey])
      ? [...formData[formKey][fieldKey]]
      : formData[formKey]?.[fieldKey]
      ? [formData[formKey][fieldKey]]
      : [];

    let updatedImages;
    if (index !== null) {
      // Update specific image at index
      updatedImages = [...currentImages];
      updatedImages[index] = file;
    } else {
      // Add new image
      updatedImages = [...currentImages, file];
    }

    setFormData({
      ...formData,
      [formKey]: {
        ...(formData[formKey] || {}),
        [fieldKey]: updatedImages,
      },
    });
  };

  // Function to remove image at specific index
  const removeImage = (fieldKey, index) => {
    const currentImages = [...formData[formKey][fieldKey]];
    currentImages.splice(index, 1);

    setFormData({
      ...formData,
      [formKey]: {
        ...(formData[formKey] || {}),
        [fieldKey]: currentImages.length > 0 ? currentImages : [],
      },
    });
  };

  const handleArrayChange = (arrayName, index, fieldName, value) => {
    const arrayData = [...(formData[formKey]?.[arrayName] || [])];

    while (arrayData.length <= index) {
      arrayData.push({});
    }

    arrayData[index] = {
      ...arrayData[index],
      [fieldName]: value,
    };

    setFormData({
      ...formData,
      [formKey]: {
        ...(formData[formKey] || {}),
        [arrayName]: arrayData,
      },
    });
  };

  // New function to handle multiple images within an array item
  const handleArrayMultipleImages = (
    arrayName,
    index,
    fieldName,
    file,
    imageIndex = null
  ) => {
    const arrayData = [...(formData[formKey]?.[arrayName] || [])];

    while (arrayData.length <= index) {
      arrayData.push({});
    }

    const currentImages = Array.isArray(arrayData[index][fieldName])
      ? [...arrayData[index][fieldName]]
      : arrayData[index][fieldName]
      ? [arrayData[index][fieldName]]
      : [];

    let updatedImages;
    if (imageIndex !== null) {
      // Update specific image at index
      updatedImages = [...currentImages];
      updatedImages[imageIndex] = file;
    } else {
      // Add new image
      updatedImages = [...currentImages, file];
    }

    arrayData[index] = {
      ...arrayData[index],
      [fieldName]: updatedImages,
    };

    setFormData({
      ...formData,
      [formKey]: {
        ...(formData[formKey] || {}),
        [arrayName]: arrayData,
      },
    });
  };

  // Function to remove image from an array item
  const removeArrayImage = (arrayName, index, fieldName, imageIndex) => {
    const arrayData = [...(formData[formKey]?.[arrayName] || [])];

    if (arrayData[index] && arrayData[index][fieldName]) {
      const currentImages = [...arrayData[index][fieldName]];
      currentImages.splice(imageIndex, 1);

      arrayData[index] = {
        ...arrayData[index],
        [fieldName]: currentImages.length > 0 ? currentImages : [],
      };

      setFormData({
        ...formData,
        [formKey]: {
          ...(formData[formKey] || {}),
          [arrayName]: arrayData,
        },
      });
    }
  };

  const handleNestedArrayChange = (
    parentArrayName,
    parentIndex,
    childArrayName,
    childIndex,
    fieldName,
    value
  ) => {
    const parentArray = [...(formData[formKey]?.[parentArrayName] || [])];

    while (parentArray.length <= parentIndex) {
      parentArray.push({});
    }

    if (!parentArray[parentIndex][childArrayName]) {
      parentArray[parentIndex][childArrayName] = [];
    }

    const childArray = [...parentArray[parentIndex][childArrayName]];

    while (childArray.length <= childIndex) {
      childArray.push({});
    }

    childArray[childIndex] = {
      ...childArray[childIndex],
      [fieldName]: value,
    };

    parentArray[parentIndex][childArrayName] = childArray;

    setFormData({
      ...formData,
      [formKey]: {
        ...(formData[formKey] || {}),
        [parentArrayName]: parentArray,
      },
    });
  };

  // New function to handle multiple images in nested arrays
  const handleNestedArrayMultipleImages = (
    parentArrayName,
    parentIndex,
    childArrayName,
    childIndex,
    fieldName,
    file,
    imageIndex = null
  ) => {
    const parentArray = [...(formData[formKey]?.[parentArrayName] || [])];

    while (parentArray.length <= parentIndex) {
      parentArray.push({});
    }

    if (!parentArray[parentIndex][childArrayName]) {
      parentArray[parentIndex][childArrayName] = [];
    }

    const childArray = [...parentArray[parentIndex][childArrayName]];

    while (childArray.length <= childIndex) {
      childArray.push({});
    }

    const currentImages = Array.isArray(childArray[childIndex][fieldName])
      ? [...childArray[childIndex][fieldName]]
      : childArray[childIndex][fieldName]
      ? [childArray[childIndex][fieldName]]
      : [];

    let updatedImages;
    if (imageIndex !== null) {
      // Update specific image at index
      updatedImages = [...currentImages];
      updatedImages[imageIndex] = file;
    } else {
      // Add new image
      updatedImages = [...currentImages, file];
    }

    childArray[childIndex] = {
      ...childArray[childIndex],
      [fieldName]: updatedImages,
    };

    parentArray[parentIndex][childArrayName] = childArray;

    setFormData({
      ...formData,
      [formKey]: {
        ...(formData[formKey] || {}),
        [parentArrayName]: parentArray,
      },
    });
  };

  // Function to remove image from a nested array item
  const removeNestedArrayImage = (
    parentArrayName,
    parentIndex,
    childArrayName,
    childIndex,
    fieldName,
    imageIndex
  ) => {
    const parentArray = [...(formData[formKey]?.[parentArrayName] || [])];

    if (parentArray[parentIndex] && parentArray[parentIndex][childArrayName]) {
      const childArray = [...parentArray[parentIndex][childArrayName]];

      if (childArray[childIndex] && childArray[childIndex][fieldName]) {
        const currentImages = [...childArray[childIndex][fieldName]];
        currentImages.splice(imageIndex, 1);

        childArray[childIndex] = {
          ...childArray[childIndex],
          [fieldName]: currentImages.length > 0 ? currentImages : [],
        };

        parentArray[parentIndex][childArrayName] = childArray;

        setFormData({
          ...formData,
          [formKey]: {
            ...(formData[formKey] || {}),
            [parentArrayName]: parentArray,
          },
        });
      }
    }
  };

  const addArrayItem = (arrayName) => {
    const arrayData = [...(formData[formKey]?.[arrayName] || []), {}];

    setFormData({
      ...formData,
      [formKey]: {
        ...(formData[formKey] || {}),
        [arrayName]: arrayData,
      },
    });
  };

  const removeArrayItem = (arrayName, index) => {
    const arrayData = [...(formData[formKey]?.[arrayName] || [])];
    arrayData.splice(index, 1);

    setFormData({
      ...formData,
      [formKey]: {
        ...(formData[formKey] || {}),
        [arrayName]: arrayData,
      },
    });
  };

  const addNestedArrayItem = (parentArrayName, parentIndex, childArrayName) => {
    const parentArray = [...(formData[formKey]?.[parentArrayName] || [])];

    while (parentArray.length <= parentIndex) {
      parentArray.push({});
    }

    if (!parentArray[parentIndex][childArrayName]) {
      parentArray[parentIndex][childArrayName] = [];
    }

    parentArray[parentIndex][childArrayName] = [
      ...parentArray[parentIndex][childArrayName],
      {},
    ];

    setFormData({
      ...formData,
      [formKey]: {
        ...(formData[formKey] || {}),
        [parentArrayName]: parentArray,
      },
    });
  };

  const removeNestedArrayItem = (
    parentArrayName,
    parentIndex,
    childArrayName,
    childIndex
  ) => {
    const parentArray = [...(formData[formKey]?.[parentArrayName] || [])];

    if (parentArray[parentIndex] && parentArray[parentIndex][childArrayName]) {
      const childArray = [...parentArray[parentIndex][childArrayName]];
      childArray.splice(childIndex, 1);
      parentArray[parentIndex][childArrayName] = childArray;

      setFormData({
        ...formData,
        [formKey]: {
          ...(formData[formKey] || {}),
          [parentArrayName]: parentArray,
        },
      });
    }
  };

  const renderElement = (element, fieldKey) => {
    if (["image", "media"].includes(element.type)) {
      // Handle multi-image case
      if (element.multiple) {
        const images = Array.isArray(formData[formKey]?.[fieldKey])
          ? formData[formKey][fieldKey]
          : formData[formKey]?.[fieldKey]
          ? [formData[formKey][fieldKey]]
          : [];

        return (
          <div className={`col-${element.col} mb-3`}>
            <div>
              <label className="form-label mb-2">
                {element.label || fieldKey}
              </label>
              <div className="row flex-wrap gap-2 mb-2">
                {images.map((img, idx) => (
                  <div
                    key={`img-${fieldKey}-${idx}`}
                    className="position-relative col-3"
                  >
                    <SimpleUploader
                      {...element}
                      labelinput={`Image ${idx + 1}`}
                      image={img}
                      handleImage={(file) =>
                        handleMultipleImages(fieldKey, file, idx)
                      }
                    />
                    <Button
                      variant="danger"
                      size="sm"
                      className="position-absolute top-0 end-0"
                      onClick={() => removeImage(fieldKey, idx)}
                    >
                      ×
                    </Button>
                  </div>
                ))}
                <div>
                  <SimpleUploader
                    {...element}
                    labelinput={`Ajouter une image`}
                    image={null}
                    handleImage={(file) => handleMultipleImages(fieldKey, file)}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      } else {
        // Original single image uploader
        return (
          <div className={`col-${element.col} mb-3`}>
            <SimpleUploader
              {...element}
              labelinput={element.label}
              image={formData[formKey]?.[fieldKey] || null}
              handleImage={(file) => handleChange(fieldKey, file)}
            />
          </div>
        );
      }
    } else if (element.type === "text") {
      return (
        <div className={`col-${element.col} mb-3`}>
          <InputText
            {...element}
            labelinput={element.label}
            value={
              ["promo", "promo_amount", "price_value"]?.includes(element.name)
                ? formData?.[element.name] || ""
                : formData[formKey]?.[fieldKey] || ""
            }
            handleChange={
              ["promo", "promo_amount", "price_value"]?.includes(element.name)
                ? (value) => handleInfoChange(value, element.name)
                : (value) => handleChange(fieldKey, value)
            }
          />
        </div>
      );
    } else if (element.type === "boolean") {
      return (
        <div className={`col-${element.col} mb-3`}>
          <div className="text-start pt-2 pb-2 w-100 h-100 d-flex flex-column">
            {element.label && <label className="f16">{element.label}</label>}
            <Form.Check
              {...element}
              label={null}
              className="me-2 flex-grow-1 d-flex align-items-center"
              type="switch"
              defaultChecked={formData?.promo}
              onChange={(e) =>
                handleInfoChange(e.target.checked, e.target.name)
              }
            />
          </div>
        </div>
      );
    } else if (element.type === "array") {
      return renderArrayElement(element, fieldKey);
    } else if (element.type === "textarea") {
      return (
        <div className={`col-${element.col} mb-3`}>
          <Textarea
            {...element}
            labelinput={element.label}
            value={formData[formKey]?.[fieldKey] || ""}
            handleChange={(value) => handleChange(fieldKey, value)}
          />
        </div>
      );
    }

    return null;
  };

  const renderArrayElement = (element, arrayName) => {
    const arrayItems = formData[formKey]?.[arrayName] || [];

    return (
      <div className="col-12 mb-4">
        <h4 className="mb-3">{element.label || arrayName}</h4>

        {arrayItems.map((item, index) => (
          <div
            key={`${arrayName}-${index}`}
            className="border p-3 mb-3 rounded"
          >
            <div className="d-flex justify-content-between mb-3">
              <h5>Élément {index + 1}</h5>
              <Button
                variant="danger"
                size="sm"
                onClick={() => removeArrayItem(arrayName, index)}
              >
                Supprimer
              </Button>
            </div>

            <div className="row">
              {element.elements &&
                element.elements.map((subElement) => {
                  if (subElement.type === "array") {
                    return renderNestedArrayElement(
                      subElement,
                      arrayName,
                      index,
                      subElement.name
                    );
                  } else if (
                    (subElement.type === "image" ||
                      subElement.type === "media") &&
                    subElement.multiple
                  ) {
                    // Handle multi-image case in array
                    const images = Array.isArray(item[subElement.name])
                      ? item[subElement.name]
                      : item[subElement.name]
                      ? [item[subElement.name]]
                      : [];

                    return (
                      <div
                        key={`${arrayName}-${index}-${subElement.name}`}
                        className={`col-${subElement.col} mb-3`}
                      >
                        <div>
                          <label className="form-label mb-2">
                            {subElement.label || subElement.name}
                          </label>
                          <div className="row flex-wrap g-2 mb-2">
                            {images.map((img, imgIdx) => (
                              <div
                                key={`img-${arrayName}-${index}-${subElement.name}-${imgIdx}`}
                                className="position-relative col-3"
                              >
                                <SimpleUploader
                                  {...subElement}
                                  labelinput={`Image ${imgIdx + 1}`}
                                  image={img}
                                  handleImage={(file) =>
                                    handleArrayMultipleImages(
                                      arrayName,
                                      index,
                                      subElement.name,
                                      file,
                                      imgIdx
                                    )
                                  }
                                />
                                <Button
                                  variant="danger"
                                  size="sm"
                                  className="position-absolute top-0 end-0"
                                  onClick={() =>
                                    removeArrayImage(
                                      arrayName,
                                      index,
                                      subElement.name,
                                      imgIdx
                                    )
                                  }
                                >
                                  ×
                                </Button>
                              </div>
                            ))}
                            <div className="col-3">
                              <SimpleUploader
                                {...subElement}
                                labelinput={`Ajouter une image`}
                                image={null}
                                handleImage={(file) =>
                                  handleArrayMultipleImages(
                                    arrayName,
                                    index,
                                    subElement.name,
                                    file
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  } else {
                    return (
                      <div
                        key={`${arrayName}-${index}-${subElement.name}`}
                        className={`col-${subElement.col} mb-3`}
                      >
                        {subElement.type === "image" ||
                        subElement.type === "media" ? (
                          <SimpleUploader
                            {...element}
                            resolution={subElement.resolution}
                            labelinput={subElement.label}
                            image={item[subElement.name] || null}
                            handleImage={(file) =>
                              handleArrayChange(
                                arrayName,
                                index,
                                subElement.name,
                                file
                              )
                            }
                          />
                        ) : (
                          <InputText
                            {...subElement}
                            labelinput={subElement.label}
                            value={item[subElement.name] || ""}
                            handleChange={(value) =>
                              handleArrayChange(
                                arrayName,
                                index,
                                subElement.name,
                                value
                              )
                            }
                          />
                        )}
                      </div>
                    );
                  }
                })}
            </div>
          </div>
        ))}

        <Button
          variant="primary"
          onClick={() => addArrayItem(arrayName)}
          className="mb-3 text-white"
        >
          Ajouter un élément
        </Button>
      </div>
    );
  };

  const renderNestedArrayElement = (
    element,
    parentArrayName,
    parentIndex,
    childArrayName
  ) => {
    const parentItem =
      formData[formKey]?.[parentArrayName]?.[parentIndex] || {};
    const childItems = parentItem[childArrayName] || [];

    return (
      <div className="col-12 mb-4 border-left border-primary pl-3">
        <h5 className="mb-3">{element.label || childArrayName}</h5>

        {childItems.map((childItem, childIndex) => (
          <div
            key={`${parentArrayName}-${parentIndex}-${childArrayName}-${childIndex}`}
            className="border border-secondary p-3 mb-3 rounded"
          >
            <div className="d-flex justify-content-between mb-3">
              <h6>Sous-élément {childIndex + 1}</h6>
              <Button
                variant="danger"
                size="sm"
                onClick={() =>
                  removeNestedArrayItem(
                    parentArrayName,
                    parentIndex,
                    childArrayName,
                    childIndex
                  )
                }
              >
                Supprimer
              </Button>
            </div>

            <div className="row">
              {element.elements &&
                element.elements.map((subElement) => {
                  if (
                    (subElement.type === "image" ||
                      subElement.type === "media") &&
                    subElement.multiple
                  ) {
                    // Handle multi-image case in nested array
                    const images = Array.isArray(childItem[subElement.name])
                      ? childItem[subElement.name]
                      : childItem[subElement.name]
                      ? [childItem[subElement.name]]
                      : [];

                    return (
                      <div
                        key={`${parentArrayName}-${parentIndex}-${childArrayName}-${childIndex}-${subElement.name}`}
                        className={`col-${subElement.col} mb-3`}
                      >
                        <div>
                          <label className="form-label mb-2">
                            {subElement.label || subElement.name}
                          </label>
                          <div className="row flex-wrap g-2 mb-2">
                            {images.map((img, imgIdx) => (
                              <div
                                key={`img-${parentArrayName}-${parentIndex}-${childArrayName}-${childIndex}-${subElement.name}-${imgIdx}`}
                                className="position-relative col-3"
                              >
                                <SimpleUploader
                                  {...subElement}
                                  labelinput={`Image ${imgIdx + 1}`}
                                  image={img}
                                  handleImage={(file) =>
                                    handleNestedArrayMultipleImages(
                                      parentArrayName,
                                      parentIndex,
                                      childArrayName,
                                      childIndex,
                                      subElement.name,
                                      file,
                                      imgIdx
                                    )
                                  }
                                />
                                <Button
                                  variant="danger"
                                  size="sm"
                                  className="position-absolute top-0 end-0"
                                  onClick={() =>
                                    removeNestedArrayImage(
                                      parentArrayName,
                                      parentIndex,
                                      childArrayName,
                                      childIndex,
                                      subElement.name,
                                      imgIdx
                                    )
                                  }
                                >
                                  ×
                                </Button>
                              </div>
                            ))}
                            <div className="col-3">
                              <SimpleUploader
                                {...subElement}
                                labelinput={`Ajouter une image`}
                                image={null}
                                handleImage={(file) =>
                                  handleNestedArrayMultipleImages(
                                    parentArrayName,
                                    parentIndex,
                                    childArrayName,
                                    childIndex,
                                    subElement.name,
                                    file
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  } else {
                    return (
                      <div
                        key={`${parentArrayName}-${parentIndex}-${childArrayName}-${childIndex}-${subElement.name}`}
                        className={`col-${subElement.col} mb-3`}
                      >
                        {subElement.type === "image" ||
                        subElement.type === "media" ? (
                          <SimpleUploader
                            resolution={subElement.resolution}
                            labelinput={subElement.label}
                            image={childItem[subElement.name] || null}
                            handleImage={(file) =>
                              handleNestedArrayChange(
                                parentArrayName,
                                parentIndex,
                                childArrayName,
                                childIndex,
                                subElement.name,
                                file
                              )
                            }
                          />
                        ) : (
                          <InputText
                            {...subElement}
                            labelinput={subElement.label}
                            value={childItem[subElement.name] || ""}
                            handleChange={(value) =>
                              handleNestedArrayChange(
                                parentArrayName,
                                parentIndex,
                                childArrayName,
                                childIndex,
                                subElement.name,
                                value
                              )
                            }
                          />
                        )}
                      </div>
                    );
                  }
                })}
            </div>
          </div>
        ))}

        <Button
          variant="outline-primary"
          size="sm"
          onClick={() =>
            addNestedArrayItem(parentArrayName, parentIndex, childArrayName)
          }
          className="mb-3"
        >
          Ajouter un sous-élément
        </Button>
      </div>
    );
  };

  return (
    <Accordion className="text-start rounded-10">
      <Accordion.Item
        className="card-body accordionCustom rounded-10 p-0"
        eventKey="0"
      >
        <Accordion.Header className="rounded-10">
          <h2 className={`text-${titleColor} mb-0 f24 regular`}>{title}</h2>
        </Accordion.Header>
        <Accordion.Body>
          <div className="container-fluid">
            <div className="row">
              {formKey === "informations" && (
                <>
                  <div className="col-12">
                    <Switch
                      label="Type"
                      values={types}
                      handleChange={handleInfoChange}
                      champs="type"
                      value={formData.type}
                      required
                    />
                  </div>
                  <div className="col-12">
                    {formData?.type === 0 ? (
                      <Switch
                        label="Collection"
                        values={collections}
                        handleChange={handleInfoChange}
                        champs="collection"
                        value={formData.collection}
                        required
                      />
                    ) : null}
                    {formData?.type === 1 ? (
                      <Switch
                        label="collection"
                        values={spaDeNageCollections}
                        handleChange={handleInfoChange}
                        champs="collection"
                        value={formData.collection}
                      />
                    ) : null}
                    {formData?.type === 2 ? (
                      <Switch
                        label="collection"
                        values={saunaCollections}
                        handleChange={handleInfoChange}
                        champs="collection"
                        value={formData.collection}
                      />
                    ) : null}
                  </div>
                </>
              )}
              {elements?.map((element) => (
                <React.Fragment key={element.name}>
                  {renderElement(element, element.name)}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-center w-25 mx-auto mt-3">
            <CustomButton
              buttonText="Enregistrer"
              callBack={() =>
                updateProduct(
                  id,
                  formKey,
                  formData[formKey] || {},
                  formData?.type,
                  formData?.collection,
                  {
                    promo: formData.promo,
                    promo_amount: formData.promo_amount,
                    price_value: formData.price_value,
                  }
                )
              }
            />
          </div>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default SectionContainer;
